import { FC, useState } from "react";
import {
  Alert,
  Box,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { Email, Save } from "@mui/icons-material";
import { ClinicianDetails } from "../types";
import * as apiClient from "../api";
import { isAxiosError } from "axios";
import { useClinicianContext } from "./ClinicianContext";

interface ClinicianDetailsTabProps {
  clinicianId: string;
  clinicianDetails: ClinicianDetails;
}
const ClinicianDetailsTab: FC<ClinicianDetailsTabProps> = ({
  clinicianId,
  clinicianDetails,
}) => {
  const [, setClinician] = useClinicianContext();
  const defaultErrorMessage = "Could not update details";
  const [showSaving, setShowSaving] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMsg, setErrorMsg] = useState(defaultErrorMessage);
  const [firstName, setFirstName] = useState(clinicianDetails.firstName);
  const [lastName, setLastName] = useState(clinicianDetails.lastName);
  const [email, setEmail] = useState(clinicianDetails.email);

  const isValid = !!firstName && !!lastName && !!email;

  const newClinicianDetails = {
    firstName,
    lastName,
    email,
  };

  const edited =
    clinicianDetails.firstName !== newClinicianDetails.firstName ||
    clinicianDetails.lastName !== newClinicianDetails.lastName ||
    clinicianDetails.email !== newClinicianDetails.email;

  const handleSaveClick = async () => {
    setShowSaving(true);

    try {
      await apiClient.updateClinician(clinicianId, newClinicianDetails);
      setClinician({ ...newClinicianDetails, clinicianId });
      setShowError(false);
      setShowSaved(true);
    } catch (e) {
      if (isAxiosError(e)) {
        setShowSaved(false);
        setErrorMsg(e?.response?.data?.message ?? defaultErrorMessage);
        setShowError(true);
      }
    } finally {
      setShowSaving(false);
    }
  };

  const handleCloseSavedAlert = () => {
    setShowSaved(false);
  };

  const handleCloseErrorAlert = () => {
    setShowError(false);
  };

  return (
    <Stack spacing={2}>
      {showSaved && (
        <Alert severity="success" onClose={handleCloseSavedAlert}>
          Successfully updated your details
        </Alert>
      )}
      {showError && (
        <Alert severity="error" onClose={handleCloseErrorAlert}>
          {errorMsg}
        </Alert>
      )}
      <Stack direction="row" spacing={8}>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="h6">General</Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <TextField
              label="First name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
            <TextField
              label="Last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </Stack>
        </Stack>
        <Stack spacing={2}>
          <Typography variant="h6">Contact</Typography>
          <TextField
            label="Email"
            value={email}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email />
                </InputAdornment>
              ),
            }}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Stack>
      </Stack>
      <Box>
        <LoadingButton
          loading={showSaving}
          disabled={!isValid || !edited}
          loadingPosition="start"
          startIcon={<Save />}
          variant="contained"
          onClick={handleSaveClick}
        >
          Save
        </LoadingButton>
      </Box>
    </Stack>
  );
};

export default ClinicianDetailsTab;
